import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {CategoryShareModeEnum} from "../../categories.dto";
import {ICreateRolesBody, IUpdateRolesBody} from '../groups/interfaces';
import {
    DocumentTypeAccessModeEnum
} from "./roles-righr-sidebar/category-by-document-type/document-type-disabled-conditionally.pipe";

@Injectable({
    providedIn: 'root'
})
export class RolesService {

    public rightSidebarActionSubject$ = new Subject<{open: boolean}>();

    constructor(private http: HttpClient) { }


    getSidebarObservable(){
        return this.rightSidebarActionSubject$.asObservable();
    }

    /**
     * fetch all permissions
     * @returns response
     */
    fetchAllPermissions(): Observable<IPermissionDTO[]> {
        return this.http.get<IPermissionDTO[]>(`security/permissions`);
    }

    /**
     * Fetch groups
     * @returns response
     */
    fetchRoles(): Observable<IGetRolesResponse[]> {
        return this.http.get<IGetRolesResponse[]>(`security/roles`);
    }

    /**
     * set role as default
     * @param roleId
     * @returns response
     */
    setDefault(roleId: number) {
        return this.http.put(`security/roles/${roleId}/default`, '');
    }

    /**
     * create role
     * @returns response
     */
    createRole(formData: ICreateRolesBody) {
        return this.http.post<IGetRolesResponse>('security/roles', formData);
    }

    /**
     * Delete role
     * @param roleId
     * @returns response
     */
    deleteRole(roleId: number) {
        return this.http.delete(`security/roles/${roleId}`);
    }

    /**
     * Update role
     * @returns response
     */
    updateRole(formData: IUpdateRolesBody) {
        return this.http.put<IUpdateRolesBody>(`security/roles/${formData.id}`, formData);
    }

    /**
     * update permission for sepcific role
     * @param roleId
     * @param permissionData
     * @returns response
     */
    updatePermission(roleId: number, permissionData: IUpdatePermissionsRequest) {
        return this.http.put(`security/roles/${roleId}/managePermissions`, permissionData);
    }

    getUsersInWorkspace(workspaceId: number) {
        return this.http.get<{ id: number, name: string }[]>(`workspaces/${workspaceId}/users`);

    }
}

export interface IUpdatePermissionsRequest {
    permissions: number[],
    shared_templates: number[],
    templates_access: IDirectAccessForDocumentType,
    documents_access: IDirectAccessForDocumentType,
    contracts_access: IDirectAccessForDocumentType,
}

export interface IAccessType{
    id?: number,
    document_type_id:number,
    category_share_mode:CategoryShareModeEnum,
    categories?: number[]
}

export interface IDirectAccessForDocumentType {
    access_types:  IAccessType[],
    categories?: number[],
    access_level: AccessLevelEnum
    users: number[],
    groups: number[]
}

export interface IDocumentIDirectAccessForDocumentType extends IDirectAccessForDocumentType{
    has_all_document_types_access:boolean,
}

export interface IContractIDirectAccessForDocumentType extends IDirectAccessForDocumentType{
    has_all_contract_types_access:boolean,
}

export interface ITemplateIDirectAccessForDocumentType extends IDirectAccessForDocumentType{
    has_all_template_types_access?:boolean,
}

export enum AccessLevelEnum {
    'full_access' = 'full_access',
    'view' = 'view',
    'personal' = 'personal',
    'edit' = 'edit',
    'use_only' = 'use_only',
    'use_and_edit' = 'use_and_edit'
}


export const AccessLevelPriority = [
    AccessLevelEnum.full_access,
    AccessLevelEnum.edit,
    AccessLevelEnum.use_and_edit,
    AccessLevelEnum.personal,
    AccessLevelEnum.view,
    AccessLevelEnum.use_only
];


export interface IGetRolesResponse {
    id: number;
    name: string;
    predefined: boolean;
    default: boolean;
    description: string;
    templates_access_level: AccessLevelEnum,
    documents_access_level: AccessLevelEnum,
    contracts_access_level: AccessLevelEnum,
    document_categories: number[],
    permissions: IPermissionDTO[],
    template_types: IAccessType [],
    template_users: number[],
    template_groups: number[],
    document_types: IAccessType[],
    document_users: number[],
    document_groups: number[],
    contract_types: IAccessType[],
    contract_users: number[],
    contract_groups: number[],
    shared_templates:number[],
    has_all_contract_types_access:boolean,
    has_all_document_types_access:boolean,
    has_all_template_types_access:boolean,
}


export interface IPermissionDTO {
    id: number
    name: string
}
